import { useState } from "react";
import { API } from "../config";
import { useLanguage } from "../contexts/LanguageContext";
import { TrendingUp, Download, CheckCircle, Shield, Eye, Zap, Globe, BookOpen, Target, FileText, Mic, ShoppingCart, Store, Copy, Check, Webhook, Code2, Layout, Palette } from "lucide-react";

export default function PluginPage() {
  const { t } = useLanguage();
  const [downloading, setDownloading] = useState(false);

  const FEATURES = [
    { icon: Eye, title: t.pl_f1_t || "AI Visibility Tracking", desc: t.pl_f1_d || "See if ChatGPT, Perplexity & Gemini cite your website", free: true },
    { icon: Shield, title: t.pl_f2_t || "Complete SEO Audit", desc: t.pl_f2_d || "Technical analysis with actionable recommendations", free: true },
    { icon: BookOpen, title: t.pl_f3_t || "Keyword Research", desc: t.pl_f3_d || "Find better keywords with volume, difficulty & trends", free: "3 keywords" },
    { icon: Mic, title: t.pl_f4_t || "Voice Search Score", desc: t.pl_f4_d || "Optimize for Siri, Alexa, Google Assistant", free: true },
    { icon: Target, title: t.pl_f5_t || "AI Recommendations", desc: t.pl_f5_d || "Step-by-step instructions to improve your score", free: "2 of 5" },
    { icon: Zap, title: t.pl_f6_t || "Dashboard Widget", desc: t.pl_f6_d || "SEO score directly in your WordPress dashboard", free: true },
    { icon: Globe, title: t.pl_f7_t || "Auto-Scan on Publish", desc: t.pl_f7_d || "Automatically analyzes new posts when published", free: true },
    { icon: ShoppingCart, title: t.pl_f8_t || "WooCommerce SEO", desc: t.pl_f8_d || "Product SEO scores, AI descriptions & Google Shopping optimization", free: true },
    { icon: FileText, title: t.pl_f9_t || "Blog Agent (€25)", desc: t.pl_f9_d || "25 SEO articles with backlinks to your site", free: false },
  ];

  const shopifySnippet = `<script src="${API}/shopify/embed.js" data-email="your@email.com"></script>`;
  const webflowSnippet = `<script src="${API}/webflow/embed.js" data-email="your@email.com" data-collection="YOUR_COLLECTION_ID"></script>`;
  const wixSnippet = `<script src="${API}/wix/embed.js" data-email="your@email.com" data-site="YOUR_SITE_ID"></script>`;
  const webhookSnippet = `curl -X POST https://toprankpulse.com/api/webhooks/publish \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"target_url": "https://your-webhook-endpoint.com/blog", "article_id": "ARTICLE_ID"}'`;

  const [copiedId, setCopiedId] = useState(null);
  const copyCode = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDownload = () => {
    setDownloading(true);
    window.location.href = `${API}/plugin/download`;
    setTimeout(() => setDownloading(false), 3000);
  };

  const handleCopy = () => {
    copyCode(shopifySnippet, 'shopify');
  };

  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-white">
      {/* Header */}
      <header className="bg-[var(--color-bg)]/60 backdrop-blur-xl border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#a855f7] flex items-center justify-center"><TrendingUp className="w-5 h-5 text-white" /></div>
            <span className="text-xl font-black font-['Cabinet_Grotesk']">TopRankPulse</span>
          </a>
          <div className="flex items-center gap-4">
            <a href="/" className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] font-['JetBrains_Mono'] uppercase">Home</a>
            <a href="/blog" className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] font-['JetBrains_Mono'] uppercase">Blog</a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#a855f7]/10 text-[#a855f7] font-['JetBrains_Mono'] text-sm tracking-[0.2em] uppercase mb-6">
            {t.pl_badge || "WordPress Plugin"}
          </span>
          <h1 className="text-5xl sm:text-6xl font-black font-['Cabinet_Grotesk'] tracking-tighter leading-[0.9] mb-6">
            {t.pl_title_1 || "FREE AI SEO"}<br />
            <span className="text-[#a855f7]">{t.pl_title_2 || "VISIBILITY TRACKER"}</span><br />
            {t.pl_title_3 || "FOR WORDPRESS"}
          </h1>
          <p className="text-xl text-[var(--color-text-muted)] mb-8 max-w-2xl mx-auto">
            {t.pl_desc || "Install in 30 seconds. See if ChatGPT, Perplexity & Gemini recommend your content. Get actionable fixes."}
          </p>

          <button
            onClick={handleDownload}
            disabled={downloading}
            className="inline-flex items-center gap-3 px-10 py-5 bg-[#a855f7] text-white font-black text-xl uppercase tracking-wider hover:bg-purple-600 hover:scale-[1.02] transition-all disabled:opacity-50"
            data-testid="download-plugin-btn"
          >
            <Download className="w-6 h-6" />
            {downloading ? (t.pl_downloading || 'DOWNLOADING...') : (t.pl_download || 'DOWNLOAD FREE PLUGIN').toUpperCase()}
          </button>
          <p className="text-[var(--color-text-muted)] text-sm mt-3 font-['JetBrains_Mono']">v1.0.0 — WordPress 5.0+ — PHP 7.4+ — 13 KB</p>
        </div>
      </section>

      {/* Installation */}
      <section className="py-20 px-6 bg-[var(--color-surface)] border-y border-[var(--color-border)]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-black font-['Cabinet_Grotesk'] tracking-tighter text-center mb-12">
            INSTALL IN <span className="text-[#FFE600]">60 SECONDS</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Download & Install", desc: "Download the zip file. In WordPress go to Plugins → Add New → Upload Plugin → Choose file → Install." },
              { step: "02", title: "Enter Email", desc: "Go to TopRankPulse in admin menu → Settings → Enter your email. This connects your site to our platform." },
              { step: "03", title: t.pl_s3_t || "Scan Your Site", desc: t.pl_s3_d || "Click 'Scan Now' and get your SEO score + AI visibility report in 30 seconds. Free, no credit card." },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <span className="text-[#a855f7] font-['JetBrains_Mono'] text-4xl font-bold">{s.step}</span>
                <h3 className="text-white font-bold mt-3 mb-2 font-['Cabinet_Grotesk']">{s.title}</h3>
                <p className="text-[var(--color-text-muted)] text-sm">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-black font-['Cabinet_Grotesk'] tracking-tighter text-center mb-12">WHAT'S INSIDE</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {FEATURES.map((f, i) => (
              <div key={i} className="bg-[var(--color-surface)] border border-[var(--color-border)] p-6 flex items-start gap-4 hover:border-[var(--color-primary)] transition-colors" data-testid={`feature-${i}`}>
                <f.icon className="w-6 h-6 text-[var(--color-text-muted)] flex-shrink-0 mt-1" />
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-white font-['Cabinet_Grotesk']">{f.title}</h3>
                    {f.free === true ? (
                      <span className="text-[10px] px-2 py-0.5 bg-emerald-900/30 text-emerald-400 font-bold uppercase">FREE</span>
                    ) : f.free === false ? (
                      <span className="text-[10px] px-2 py-0.5 bg-[#FFE600]/10 text-[#FFE600] font-bold uppercase">PAID</span>
                    ) : (
                      <span className="text-[10px] px-2 py-0.5 bg-[var(--color-surface-alt)] text-[var(--color-text-muted)] font-bold uppercase">FREE: {f.free}</span>
                    )}
                  </div>
                  <p className="text-[var(--color-text-muted)] text-sm mt-1">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WooCommerce */}
      <section className="py-20 px-6 bg-[#0d0d0d]" data-testid="woocommerce-section">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-4">
            <ShoppingCart className="w-8 h-8 text-[#7F54B3]" />
            <h2 className="text-3xl font-black font-['Cabinet_Grotesk'] tracking-tighter">
              WOOCOMMERCE <span className="text-[#7F54B3]">INTEGRATION</span>
            </h2>
          </div>
          <p className="text-[var(--color-text-muted)] text-center mb-10 max-w-2xl mx-auto">
            The WordPress plugin automatically detects WooCommerce and adds product-level SEO tools.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: t.pl_w1_t || "Product SEO Score", desc: t.pl_w1_d || "Each product gets a real-time SEO score based on title, description, images, price & categories." },
              { title: t.pl_w2_t || "AI Description Generator", desc: t.pl_w2_d || "One-click AI-generated SEO product descriptions optimized for search engines and AI assistants." },
              { title: t.pl_w3_t || "Google Shopping Ready", desc: t.pl_w3_d || "Enhanced product schema markup for Google Shopping, rich snippets & AI search visibility." },
            ].map((f, i) => (
              <div key={i} className="bg-[var(--color-surface)] border border-[var(--color-border)] p-6 hover:border-[#7F54B3]/40 transition-colors">
                <h3 className="font-bold text-white font-['Cabinet_Grotesk'] mb-2">{f.title}</h3>
                <p className="text-[var(--color-text-muted)] text-sm">{f.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-[var(--color-text-muted)] text-sm mt-6 font-['JetBrains_Mono']">
            Included free in the WordPress plugin — just activate WooCommerce and TopRankPulse
          </p>
        </div>
      </section>

      {/* Shopify */}
      <section className="py-20 px-6 bg-[var(--color-surface)] border-y border-[var(--color-border)]" data-testid="shopify-section">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Store className="w-8 h-8 text-[#95BF47]" />
            <h2 className="text-3xl font-black font-['Cabinet_Grotesk'] tracking-tighter">
              SHOPIFY <span className="text-[#95BF47]">EMBED</span>
            </h2>
          </div>
          <p className="text-[var(--color-text-muted)] text-center mb-10 max-w-2xl mx-auto">
            Add a floating SEO widget to your Shopify store. One line of code — instant SEO analysis.
          </p>

          <div className="grid md:grid-cols-3 gap-8 mb-10">
            {[
              { step: "01", title: "Copy the Script", desc: "Copy the embed code below and replace your@email.com with your email." },
              { step: "02", title: "Paste in Shopify", desc: "Go to Shopify Admin → Settings → Custom Code → paste into the <head> section." },
              { step: "03", title: "SEO Widget Active", desc: "A floating button appears on your store. Click to scan and see your SEO score." },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <span className="text-[#95BF47] font-['JetBrains_Mono'] text-4xl font-bold">{s.step}</span>
                <h3 className="text-white font-bold mt-3 mb-2 font-['Cabinet_Grotesk']">{s.title}</h3>
                <p className="text-[var(--color-text-muted)] text-sm">{s.desc}</p>
              </div>
            ))}
          </div>

          <div className="bg-black border border-[var(--color-border)] p-1" data-testid="shopify-code-block">
            <div className="flex items-center justify-between px-4 py-2 bg-[var(--color-surface)] border-b border-[var(--color-border)]">
              <span className="text-xs text-[var(--color-text-muted)] font-['JetBrains_Mono'] uppercase tracking-widest">Embed Code</span>
              <button
                onClick={handleCopy}
                className="flex items-center gap-1.5 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors font-['JetBrains_Mono']"
                data-testid="copy-shopify-code-btn"
              >
                {copiedId === 'shopify' ? <><Check className="w-3.5 h-3.5 text-emerald-400" />{t.pl_copied || "Copied!"}</> : <><Copy className="w-3.5 h-3.5" />{t.pl_copy || "Copy Code"}</>}
              </button>
            </div>
            <pre className="p-4 overflow-x-auto text-sm font-['JetBrains_Mono'] text-[#95BF47]">
              <code>{shopifySnippet}</code>
            </pre>
          </div>
        </div>
      </section>

      {/* Webflow */}
      <section className="py-20 px-6 bg-[var(--color-bg)]" data-testid="webflow-section">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Layout className="w-8 h-8 text-[#4353FF]" />
            <h2 className="text-3xl font-black font-['Cabinet_Grotesk'] tracking-tighter">
              {t.pl_wf_title || "WEBFLOW"} <span className="text-[#4353FF]">{t.pl_wf_title2 || "CMS PUBLISH"}</span>
            </h2>
          </div>
          <p className="text-[var(--color-text-muted)] text-center mb-4 max-w-2xl mx-auto">
            {t.pl_wf_desc || "Blog Agent publishes SEO articles directly to your Webflow CMS collection. Articles include 2 AI-generated images and naturally embedded backlinks."}
          </p>
          <div className="flex justify-center gap-3 mb-10">
            <span className="text-[10px] px-3 py-1 bg-emerald-900/30 text-emerald-400 font-bold uppercase font-['JetBrains_Mono']">{t.pl_auto_publish || "Auto-Publish"}</span>
            <span className="text-[10px] px-3 py-1 bg-blue-900/30 text-blue-400 font-bold uppercase font-['JetBrains_Mono']">{t.pl_ai_images || "2 AI Images"}</span>
            <span className="text-[10px] px-3 py-1 bg-purple-900/30 text-purple-400 font-bold uppercase font-['JetBrains_Mono']">{t.pl_backlinks || "2 SEO Backlinks"}</span>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-10">
            {[
              { step: "01", title: t.pl_wf_s1 || "Get API Key", desc: t.pl_wf_s1d || "In Webflow: Site Settings → API → Generate API Key. Copy the key." },
              { step: "02", title: t.pl_wf_s2 || "Add Collection ID", desc: t.pl_wf_s2d || "Go to your Blog CMS Collection → Settings → Copy the Collection ID." },
              { step: "03", title: t.pl_wf_s3 || "Connect in Dashboard", desc: t.pl_wf_s3d || "In TopRankPulse Dashboard → Blog Agent → Publish Target → Webflow → Paste keys." },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <span className="text-[#4353FF] font-['JetBrains_Mono'] text-4xl font-bold">{s.step}</span>
                <h3 className="text-white font-bold mt-3 mb-2 font-['Cabinet_Grotesk']">{s.title}</h3>
                <p className="text-[var(--color-text-muted)] text-sm">{s.desc}</p>
              </div>
            ))}
          </div>

          <div className="bg-black border border-[var(--color-border)] p-1" data-testid="webflow-code-block">
            <div className="flex items-center justify-between px-4 py-2 bg-[var(--color-surface)] border-b border-[var(--color-border)]">
              <span className="text-xs text-[var(--color-text-muted)] font-['JetBrains_Mono'] uppercase tracking-widest">{t.pl_embed_code || "Embed Code"}</span>
              <button onClick={() => copyCode(webflowSnippet, 'webflow')} className="flex items-center gap-1.5 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors font-['JetBrains_Mono']" data-testid="copy-webflow-code-btn">
                {copiedId === 'webflow' ? <><Check className="w-3.5 h-3.5 text-emerald-400" />{t.pl_copied || "Copied!"}</> : <><Copy className="w-3.5 h-3.5" />{t.pl_copy || "Copy Code"}</>}
              </button>
            </div>
            <pre className="p-4 overflow-x-auto text-sm font-['JetBrains_Mono'] text-[#4353FF]"><code>{webflowSnippet}</code></pre>
          </div>
        </div>
      </section>

      {/* Wix */}
      <section className="py-20 px-6 bg-[var(--color-surface)] border-y border-[var(--color-border)]" data-testid="wix-section">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Palette className="w-8 h-8 text-[#FAAD4D]" />
            <h2 className="text-3xl font-black font-['Cabinet_Grotesk'] tracking-tighter">
              {t.pl_wix_title || "WIX"} <span className="text-[#FAAD4D]">{t.pl_wix_title2 || "BLOG PUBLISH"}</span>
            </h2>
          </div>
          <p className="text-[var(--color-text-muted)] text-center mb-4 max-w-2xl mx-auto">
            {t.pl_wix_desc || "Automatically publish Blog Agent articles to your Wix blog. Full content with images and SEO-optimized backlinks."}
          </p>
          <div className="flex justify-center gap-3 mb-10">
            <span className="text-[10px] px-3 py-1 bg-emerald-900/30 text-emerald-400 font-bold uppercase font-['JetBrains_Mono']">{t.pl_auto_publish || "Auto-Publish"}</span>
            <span className="text-[10px] px-3 py-1 bg-blue-900/30 text-blue-400 font-bold uppercase font-['JetBrains_Mono']">{t.pl_ai_images || "2 AI Images"}</span>
            <span className="text-[10px] px-3 py-1 bg-purple-900/30 text-purple-400 font-bold uppercase font-['JetBrains_Mono']">{t.pl_backlinks || "2 SEO Backlinks"}</span>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-10">
            {[
              { step: "01", title: t.pl_wix_s1 || "Get Site ID", desc: t.pl_wix_s1d || "In Wix: Dashboard → Settings → Advanced → Your Site ID is in the URL." },
              { step: "02", title: t.pl_wix_s2 || "Enable Custom Code", desc: t.pl_wix_s2d || "Go to Settings → Custom Code → Add code to <head> section." },
              { step: "03", title: t.pl_wix_s3 || "Connect in Dashboard", desc: t.pl_wix_s3d || "In TopRankPulse Dashboard → Blog Agent → Publish Target → Wix → Paste Site ID." },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <span className="text-[#FAAD4D] font-['JetBrains_Mono'] text-4xl font-bold">{s.step}</span>
                <h3 className="text-white font-bold mt-3 mb-2 font-['Cabinet_Grotesk']">{s.title}</h3>
                <p className="text-[var(--color-text-muted)] text-sm">{s.desc}</p>
              </div>
            ))}
          </div>

          <div className="bg-black border border-[var(--color-border)] p-1" data-testid="wix-code-block">
            <div className="flex items-center justify-between px-4 py-2 bg-[var(--color-surface)] border-b border-[var(--color-border)]">
              <span className="text-xs text-[var(--color-text-muted)] font-['JetBrains_Mono'] uppercase tracking-widest">{t.pl_embed_code || "Embed Code"}</span>
              <button onClick={() => copyCode(wixSnippet, 'wix')} className="flex items-center gap-1.5 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors font-['JetBrains_Mono']" data-testid="copy-wix-code-btn">
                {copiedId === 'wix' ? <><Check className="w-3.5 h-3.5 text-emerald-400" />{t.pl_copied || "Copied!"}</> : <><Copy className="w-3.5 h-3.5" />{t.pl_copy || "Copy Code"}</>}
              </button>
            </div>
            <pre className="p-4 overflow-x-auto text-sm font-['JetBrains_Mono'] text-[#FAAD4D]"><code>{wixSnippet}</code></pre>
          </div>
        </div>
      </section>

      {/* Framer + Webhooks */}
      <section className="py-20 px-6 bg-[var(--color-bg)]" data-testid="webhook-section">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Webhook className="w-8 h-8 text-[#FF6B35]" />
            <h2 className="text-3xl font-black font-['Cabinet_Grotesk'] tracking-tighter">
              {t.pl_wh_title || "WEBHOOKS"} <span className="text-[#FF6B35]">{t.pl_wh_title2 || "& UNIVERSAL PUBLISH"}</span>
            </h2>
          </div>
          <p className="text-[var(--color-text-muted)] text-center mb-4 max-w-2xl mx-auto">
            {t.pl_wh_desc || "Send Blog Agent articles to any platform via webhooks. Works with Framer, Zapier, Make (Integromat), n8n, or your own backend. Each article includes full HTML content, 2 AI images, and naturally embedded backlinks."}
          </p>
          <div className="flex justify-center gap-3 mb-10 flex-wrap">
            <span className="text-[10px] px-3 py-1 bg-[var(--color-surface-alt)] text-[var(--color-text)] font-bold uppercase font-['JetBrains_Mono']">Framer</span>
            <span className="text-[10px] px-3 py-1 bg-[var(--color-surface-alt)] text-[var(--color-text)] font-bold uppercase font-['JetBrains_Mono']">Zapier</span>
            <span className="text-[10px] px-3 py-1 bg-[var(--color-surface-alt)] text-[var(--color-text)] font-bold uppercase font-['JetBrains_Mono']">Make</span>
            <span className="text-[10px] px-3 py-1 bg-[var(--color-surface-alt)] text-[var(--color-text)] font-bold uppercase font-['JetBrains_Mono']">n8n</span>
            <span className="text-[10px] px-3 py-1 bg-[var(--color-surface-alt)] text-[var(--color-text)] font-bold uppercase font-['JetBrains_Mono']">{t.pl_wh_custom || "Custom API"}</span>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-10">
            {[
              { step: "01", title: t.pl_wh_s1 || "Set Up Webhook URL", desc: t.pl_wh_s1d || "Create an endpoint that accepts POST requests. In Zapier/Make, create a new Webhook trigger and copy the URL." },
              { step: "02", title: t.pl_wh_s2 || "Connect in Dashboard", desc: t.pl_wh_s2d || "TopRankPulse Dashboard → Blog Agent → Publish Target → Webhook → Paste your endpoint URL." },
              { step: "03", title: t.pl_wh_s3 || "Auto-Publish Articles", desc: t.pl_wh_s3d || "Blog Agent sends a POST request with the article data (title, HTML content, images, backlinks) to your endpoint." },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <span className="text-[#FF6B35] font-['JetBrains_Mono'] text-4xl font-bold">{s.step}</span>
                <h3 className="text-white font-bold mt-3 mb-2 font-['Cabinet_Grotesk']">{s.title}</h3>
                <p className="text-[var(--color-text-muted)] text-sm">{s.desc}</p>
              </div>
            ))}
          </div>

          {/* Webhook JSON payload preview */}
          <div className="bg-black border border-[var(--color-border)] p-1 mb-8" data-testid="webhook-payload-block">
            <div className="flex items-center justify-between px-4 py-2 bg-[var(--color-surface)] border-b border-[var(--color-border)]">
              <span className="text-xs text-[var(--color-text-muted)] font-['JetBrains_Mono'] uppercase tracking-widest">{t.pl_wh_payload || "Webhook Payload (JSON)"}</span>
            </div>
            <pre className="p-4 overflow-x-auto text-xs font-['JetBrains_Mono'] text-[#FF6B35] leading-relaxed"><code>{`{
  "event": "article.published",
  "article": {
    "title": "7 Ways AI is Changing Local SEO",
    "slug": "ai-changing-local-seo",
    "content_html": "<p>Full article with <a href='https://yoursite.com'>backlink 1</a>...</p>",
    "excerpt": "Discover how AI search engines...",
    "seo_score": 92,
    "word_count": 2840,
    "keyword": "ai local seo",
    "images": [
      { "url": "https://cdn.toprankpulse.com/img/ai-seo-hero.jpg", "alt": "AI SEO optimization" },
      { "url": "https://cdn.toprankpulse.com/img/ai-seo-chart.jpg", "alt": "Traffic growth chart" }
    ],
    "backlinks": [
      { "anchor": "best SEO practices", "url": "https://yoursite.com/seo-guide" },
      { "anchor": "AI visibility tools", "url": "https://yoursite.com/tools" }
    ],
    "published_at": "2026-04-15T10:00:00Z"
  }
}`}</code></pre>
          </div>

          {/* API example */}
          <div className="bg-black border border-[var(--color-border)] p-1" data-testid="webhook-curl-block">
            <div className="flex items-center justify-between px-4 py-2 bg-[var(--color-surface)] border-b border-[var(--color-border)]">
              <span className="text-xs text-[var(--color-text-muted)] font-['JetBrains_Mono'] uppercase tracking-widest">API {t.pl_wh_example || "Example"}</span>
              <button onClick={() => copyCode(webhookSnippet, 'webhook')} className="flex items-center gap-1.5 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors font-['JetBrains_Mono']" data-testid="copy-webhook-code-btn">
                {copiedId === 'webhook' ? <><Check className="w-3.5 h-3.5 text-emerald-400" />{t.pl_copied || "Copied!"}</> : <><Copy className="w-3.5 h-3.5" />{t.pl_copy || "Copy Code"}</>}
              </button>
            </div>
            <pre className="p-4 overflow-x-auto text-sm font-['JetBrains_Mono'] text-emerald-400"><code>{webhookSnippet}</code></pre>
          </div>

          {/* Supported platforms grid */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-10">
            {[
              { name: "WordPress", color: "text-[#21759B]", active: true },
              { name: "Shopify", color: "text-[#95BF47]", active: true },
              { name: "Webflow", color: "text-[#4353FF]", active: true },
              { name: "Wix", color: "text-[#FAAD4D]", active: true },
              { name: "Framer", color: "text-[#FF6B35]", active: true },
            ].map((p, i) => (
              <div key={i} className="bg-[var(--color-surface)] border border-[var(--color-border)] p-3 text-center hover:border-[var(--color-primary)] transition-colors">
                <p className={`font-bold font-['Cabinet_Grotesk'] text-sm ${p.color}`}>{p.name}</p>
                <p className="text-[10px] text-emerald-500 font-['JetBrains_Mono'] mt-1">{t.pl_supported || "SUPPORTED"}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-[var(--color-bg)]">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-black font-['Cabinet_Grotesk'] tracking-tighter mb-4">
            READY TO BOOST YOUR<br /><span className="text-[#a855f7]">AI VISIBILITY</span>?
          </h2>
          <p className="text-[var(--color-text-muted)] mb-8">Join thousands of sites using TopRankPulse to dominate AI search.</p>
          <button onClick={handleDownload} className="inline-flex items-center gap-3 px-10 py-5 bg-[#a855f7] text-white font-black text-xl uppercase tracking-wider hover:bg-purple-600 transition-all" data-testid="cta-download-btn">
            <Download className="w-6 h-6" />
            DOWNLOAD FREE PLUGIN
          </button>
          <div className="flex justify-center gap-8 mt-8 text-[var(--color-text-muted)] text-sm">
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-500" />Free forever</span>
            <span className="flex items-center gap-2"><Shield className="w-4 h-4" />No credit card</span>
            <span className="flex items-center gap-2"><Globe className="w-4 h-4" />26 languages</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-[var(--color-bg)] border-t border-[var(--color-border)]">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-[#a855f7] flex items-center justify-center"><TrendingUp className="w-4 h-4 text-white" /></div>
            <span className="font-black font-['Cabinet_Grotesk']">TopRankPulse</span>
          </div>
          <p className="text-zinc-700 text-xs font-['JetBrains_Mono']">© 2026 TopRankPulse</p>
        </div>
      </footer>
    </div>
  );
}
